/**
 * The agent report page — one generic page for every agent on the template
 * (SPEC-custom-agent-template.md §5).
 *
 * Route: /prioritization/agents/:agentType
 *
 * Tabs: one "waiting for a person" list per output kind the agent produces
 * (OUTPUT_REVIEWERS — each with that kind's review verbs), then what people
 * did with its past outputs, then what it could not do and why, then any tab
 * its type adds (BEHAVIOR_REPORT_TABS). Review buttons show only to people
 * who hold one of its review roles; the database enforces the same rule.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Loader2, Play, RefreshCw, Settings } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { Button } from '../../components/ui/Button';
import { Callout, ValueMark } from '../../components/ds';
import { decisionRecordMark } from '../../lib/valueMark';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useAgentRunStore, type AgentBatchRunner } from '../../stores/prioritization/agentRunStore';
import { useAgentSettingsStore } from '../../stores/prioritization/agentSettingsStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { agentReportHref, type AgentDefinition } from '../../lib/ai/agentDefinitions';
import { hasTemplateBatchRunner, templateBatchRunnerFor } from '../../lib/agentTemplateRunners';
import { agentReviewerAllowed } from '../../lib/agentRuntimeService';
import { useAgentDefinition } from '../../hooks/useAgentDefinition';
import { BEHAVIOR_REPORT_TABS, OUTPUT_REVIEWERS, type OutputReviewer } from '../../components/agents/outputs';
import { proposedWords, reviewOutcomeWords } from '../../components/agents/outputs/types';
import { knownActorName, useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import { when } from '../../components/agents/outputs/types';
import { resolveTriggers } from '../../components/agents/template/resolve';

interface DecisionRow {
  id: string;
  object_id: string;
  field: string | null;
  output_kind: string;
  proposed_value: unknown;
  rationale: string;
  created_at: string;
  review_outcome: string | null;
  review_value: unknown;
  review_reason: string | null;
  reviewed_at: string | null;
  /** Who accepted or rejected it; for a standing rule, the administrator who set the rule (migration 920). */
  reviewed_by: string | null;
}

/** One tab per output kind the agent produces, with its count. */
function useOutputTabs(def: AgentDefinition): Array<{ key: string; label: string; reviewer: OutputReviewer; count: number }> {
  const kinds = def.outputs.filter((k) => OUTPUT_REVIEWERS[k]);
  // The kinds are fixed per definition, so the hooks below are called in a stable order.
  return kinds.map((k) => {
    const reviewer = OUTPUT_REVIEWERS[k]!;
    const count = reviewer.useCount(def.agentType);
    return { key: k, label: reviewer.label, reviewer, count };
  });
}

function Report({ def }: { def: AgentDefinition }) {
  const agentType = def.agentType;
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const items = useItemsStore((s) => s.items);
  const config = useAgentSettingsStore((s) => s.byAgent[agentType]?.config ?? null);
  const fetchConfig = useAgentSettingsStore((s) => s.fetchConfig);
  const runSlice = useAgentRunStore((s) => s.byAgent[agentType]);
  const startRun = useAgentRunStore((s) => s.startRun);
  const outputTabs = useOutputTabs(def);
  const typeTabs = BEHAVIOR_REPORT_TABS[def.behaviorKind] ?? [];

  const [tab, setTab] = useState<string>(outputTabs[0]?.key ?? 'reviewed');
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState<DecisionRow[]>([]);
  const [mayReview, setMayReview] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      // Each output kind's review list re-reads what waits for a person.
      await Promise.all(def.outputs.map((k) => OUTPUT_REVIEWERS[k]?.refresh(workspaceId, agentType)));
      const { data } = await getSupabaseClient()
        .from('agent_decision_records')
        .select('id, object_id, field, output_kind, proposed_value, rationale, created_at, review_outcome, review_value, review_reason, reviewed_at, reviewed_by')
        .eq('workspace_id', workspaceId).eq('agent_type', agentType)
        .order('created_at', { ascending: false }).limit(100);
      setDecisions((data ?? []) as DecisionRow[]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, agentType, def.outputs]);

  useEffect(() => {
    void load();
    if (workspaceId) void fetchConfig(workspaceId, agentType);
  }, [load, workspaceId, fetchConfig, agentType]);

  useEffect(() => {
    if (!workspaceId || !userId) return;
    void agentReviewerAllowed(workspaceId, agentType, userId).then(setMayReview);
  }, [workspaceId, agentType, userId, config?.reviewRoles]);

  const reviewed = useMemo(() => decisions.filter((d) => d.review_outcome), [decisions]);
  // The name behind each decision, from the record itself (never the agent's settings today).
  const reviewerIds = useMemo(() => reviewed.map((d) => d.reviewed_by), [reviewed]);
  const reviewerMap = useActorDisplayMap(reviewerIds);
  const reviewerOf = (d: DecisionRow) => knownActorName(reviewerMap, d.reviewed_by);
  const declined = useMemo(() => decisions.filter((d) => d.output_kind === 'abstain' || d.output_kind === 'error'), [decisions]);
  const titleOf = useCallback((id: string) => items.find((i) => i.id === id)?.title ?? `Item ${id.slice(0, 8)}…`, [items]);

  const canRun = useUserStore((s) => s.can)('admin.settings') && !!config?.enabled && hasTemplateBatchRunner(def) && resolveTriggers(def, config).runNow;
  const batchRunner: AgentBatchRunner = useCallback(
    (wsId, scope, onProgress, signal) => templateBatchRunnerFor(def)(wsId, scope, onProgress, signal, userId),
    [def, userId],
  );
  const runNow = () => {
    if (!workspaceId) return;
    void startRun({ agentType, workspaceId, triggeredBy: 'manual', scope: { type: 'all_empty' }, runByUserId: userId ?? null, batchRunner }).then(() => load());
  };

  if (!workspaceId) return <Callout tone="mustard">Select a workspace to see {def.name}'s report.</Callout>;

  const Icon = def.icon;
  const isRunning = runSlice?.status === 'running';
  const waiting = outputTabs.reduce((n, t) => n + t.count, 0);
  const tabs: Array<[string, string]> = [
    ...outputTabs.map((t) => [t.key, `${t.label} (${t.count})`] as [string, string]),
    ['reviewed', `What people did (${reviewed.length})`],
    ['declined', `Could not do it (${declined.length})`],
    ...typeTabs.map((t) => [t.key, t.label] as [string, string]),
  ];
  const activeOutput = outputTabs.find((t) => t.key === tab);
  const activeTypeTab = typeTabs.find((t) => t.key === tab);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<Icon className={`h-4 w-4 ${def.accent.iconText}`} />}
        iconBg={def.accent.iconBg}
        title={def.name}
        subtitle={`${waiting} waiting for a person`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            {canRun && (
              <Button variant="secondary" size="sm" onClick={runNow} disabled={isRunning}>
                {isRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} {isRunning ? 'Running…' : 'Run now'}
              </Button>
            )}
            <Link to={def.settingsHref} className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline"><Settings className="h-3.5 w-3.5" /> Settings</Link>
          </div>
        }
      />

      {!config?.enabled && (
        <Callout tone="mustard">{def.name} is off, so it proposes nothing new. What it already proposed can still be reviewed here.</Callout>
      )}

      <div className="flex flex-wrap gap-2 border-b border-[var(--rule)] pb-2">
        {tabs.map(([key, label]) => (
          <Button key={key} variant={tab === key ? 'primary' : 'ghost'} size="sm" onClick={() => setTab(key)} aria-pressed={tab === key}>{label}</Button>
        ))}
      </div>

      {loading && !decisions.length ? (
        <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : activeOutput ? (
        <activeOutput.reviewer.Component def={def} config={config} workspaceId={workspaceId} mayReview={mayReview} onChanged={() => void load()} titleOf={titleOf} />
      ) : activeTypeTab ? (
        <activeTypeTab.Component workspaceId={workspaceId} titleOf={titleOf} />
      ) : tab === 'reviewed' ? (
        reviewed.length === 0 ? <div className="card text-center py-10"><p className="text-sm text-[var(--text-ter)]">Nothing reviewed yet.</p></div> : (
          <div className="card divide-y divide-[var(--rule)]">
            {reviewed.map((d) => (
              <div key={d.id} className="py-2 text-xs flex flex-wrap items-center gap-2">
                <Link to={`/prioritization/items/${d.object_id}`} className="font-medium text-[var(--text-pri)] underline">{titleOf(d.object_id)}</Link>
                {d.field && <span className="text-[var(--text-ter)]">{d.field}</span>}
                {d.proposed_value != null && <span>proposed <strong>{proposedWords(d.proposed_value)}</strong></span>}
                {(() => { const m = decisionRecordMark(d.review_outcome, def.name, proposedWords(d.proposed_value), d.reviewed_at, reviewerOf(d)); return m && <ValueMark {...m} size={12} />; })()}
                <span className="text-[var(--text-sec)]">· {reviewOutcomeWords(d.review_outcome, reviewerOf(d))}{d.review_outcome === 'accepted_with_change' && d.review_value != null ? ` → ${String(d.review_value)}` : ''}</span>
                {d.review_reason && <span className="text-[var(--text-ter)]">· {d.review_reason}</span>}
                <span className="ml-auto text-[var(--text-ter)]">{when(d.reviewed_at ?? d.created_at)}</span>
              </div>
            ))}
          </div>
        )
      ) : (
        declined.length === 0 ? <div className="card text-center py-10"><p className="text-sm text-[var(--text-ter)]">Nothing declined so far.</p></div> : (
          <div className="card divide-y divide-[var(--rule)]">
            {declined.map((d) => (
              <div key={d.id} className="py-2 text-xs flex flex-wrap items-center gap-2">
                <Link to={`/prioritization/items/${d.object_id}`} className="font-medium text-[var(--text-pri)] underline">{titleOf(d.object_id)}</Link>
                {d.field && <span className="text-[var(--text-ter)]">{d.field}</span>}
                <span className={d.output_kind === 'error' ? 'text-[var(--coral)]' : 'text-[var(--mustard)]'}>{d.rationale}</span>
                <span className="ml-auto text-[var(--text-ter)]">{when(d.created_at)}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default function AgentReportPage() {
  const { agentType } = useParams<{ agentType?: string }>();
  const { def, loading } = useAgentDefinition(agentType);
  if (loading) return <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (!def) return <Callout tone="mustard">There is no agent called {agentType ?? '(none)'} in this workspace.</Callout>;
  return <Report key={def.agentType} def={def} />;
}

/** The old estimator-only addresses open the agent's report page. */
export function LegacyEstimatorReportRedirect() {
  const { agentType } = useParams<{ agentType?: string }>();
  return <Navigate to={agentReportHref(agentType ?? 'value_estimator')} replace />;
}
